import React, { useState, useEffect } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';
import axiosInstance from '../services/axiosInstance';
import { displayAcademicField } from '../utils/academicProfileFromApi';
import ProfileSubsectionCard from './ProfileSubsectionCard';
import {
  PROFILE_BRANCH_MENU_ITEMS,
  PROFILE_SUBCARD_SX,
  PROFILE_SUBCARD_CONTENT_SX,
} from '../utils/profileFormConstants';

const ROLES = ['student', 'faculty', 'admin', 'cbcfaculty'];

const EMPTY = {
  name: '',
  email: '',
  password: '',
  role: 'student',
  department: '',
  batch: '',
  leetcodeUsername: '',
  branch: '',
  yearOfStudy: '',
  section: '',
};

function batchStringFromUser(user) {
  if (!user || user.batch == null) return '';
  if (typeof user.batch === 'object' && user.batch.code != null) return String(user.batch.code);
  if (typeof user.batch === 'string') return user.batch;
  return '';
}

/**
 * Admin create/edit user — same shell and section structure as {@link ../pages/common/Profile.jsx}.
 */
export default function UserFormModal({
  open,
  onClose,
  initialData,
  deptOptions = [],
  batchOptions = [],
  onSaveSuccess,
}) {
  const isEdit = !!initialData;

  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      if (initialData) {
        setForm({
          name: initialData.name || '',
          email: initialData.email || '',
          password: '',
          role: initialData.role || 'student',
          department: initialData.department || '',
          batch: batchStringFromUser(initialData),
          leetcodeUsername: initialData.leetcodeUsername || '',
          branch: initialData.branch || '',
          yearOfStudy: initialData.yearOfStudy != null ? String(initialData.yearOfStudy) : '',
          section: initialData.section || '',
        });
      } else {
        setForm(EMPTY);
      }
      setError('');
    }
  }, [open, initialData]);

  const set =
    (field) =>
    (e) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.role) {
      setError('Name, Email, and Role are required.');
      return;
    }

    setSaving(true);
    setError('');

    const payload = {
      name: form.name.trim(),
      role: form.role,
      department: form.department.trim() || undefined,
      batch: form.batch.trim() || undefined,
      leetcodeUsername: form.leetcodeUsername.trim() || undefined,
      branch: form.branch.trim() || undefined,
      yearOfStudy: form.yearOfStudy ? parseInt(form.yearOfStudy, 10) : undefined,
      section: form.section.trim() || undefined,
    };

    if (form.password.trim()) {
      payload.password = form.password.trim();
    }

    try {
      if (isEdit) {
        await axiosInstance.put(`/admin/users/${initialData._id}`, { ...payload, role: form.role });
      } else {
        payload.email = form.email.trim();
        await axiosInstance.post('/admin/users', payload);
      }
      onSaveSuccess();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save user. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await handleSave();
  };

  const title = isEdit ? 'User profile' : 'New user';
  const subtitle = isEdit ? 'Manage account details' : 'Create an account';

  const enrichedBatch = initialData?.batch && typeof initialData.batch === 'object' ? initialData.batch : null;
  const batchNameResolved =
    initialData?.batchName || enrichedBatch?.batchName || enrichedBatch?.name || '';

  const collegeDisplay = isEdit ? displayAcademicField(initialData?.college) : '—';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      scroll="paper"
      PaperProps={{
        elevation: 0,
        sx: {
          m: { xs: 1, sm: 2 },
          maxWidth: '100%',
          bgcolor: 'transparent',
          boxShadow: 'none',
          overflow: 'visible',
        },
      }}
    >
      <Container maxWidth="md" disableGutters sx={{ maxHeight: '92vh', overflow: 'auto' }}>
        <Box sx={{ mt: { xs: 1, sm: 2 }, mb: { xs: 1, sm: 2 } }}>
          <Paper elevation={3} sx={{ p: 4, position: 'relative' }}>
            <IconButton
              aria-label="Close"
              onClick={onClose}
              size="small"
              sx={{ position: 'absolute', right: 16, top: 16, zIndex: 1 }}
            >
              <CloseIcon />
            </IconButton>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, pr: 5 }}>
              <Avatar
                src={initialData?.profilePic || undefined}
                alt={form.name}
                {...(form.name
                  ? {
                      children: String(form.name)
                        .trim()
                        .split(/\s+/)
                        .slice(0, 2)
                        .map((p) => p[0])
                        .join('')
                        .toUpperCase(),
                    }
                  : { children: '?' })}
                sx={{ width: 80, height: 80, mr: 3 }}
              />
              <Box>
                <Typography variant="h4" component="h1">
                  {title}
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                  {subtitle}
                </Typography>
              </Box>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    name="name"
                    value={form.name}
                    onChange={set('name')}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    value={form.email}
                    onChange={set('email')}
                    type="email"
                    required={!isEdit}
                    disabled={isEdit}
                    variant={isEdit ? 'filled' : 'outlined'}
                    InputProps={isEdit ? { readOnly: true } : undefined}
                    helperText={isEdit ? 'Email cannot be changed' : undefined}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Role"
                    name="role"
                    value={form.role}
                    onChange={set('role')}
                    required
                  >
                    {ROLES.map((r) => (
                      <MenuItem key={r} value={r}>
                        {r.charAt(0).toUpperCase() + r.slice(1)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={isEdit ? 'New Password (optional)' : 'Password'}
                    name="password"
                    value={form.password}
                    onChange={set('password')}
                    type="password"
                    autoComplete="new-password"
                    helperText={
                      isEdit ? 'Leave blank to keep the current password' : 'Optional when creating'
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    component="h2"
                    fontWeight={600}
                    sx={{ mt: 1, mb: 0, letterSpacing: '-0.01em' }}
                  >
                    Academic Details
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Roll Number"
                        value="—"
                        InputProps={{ readOnly: true }}
                        variant="filled"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Year"
                        value="—"
                        InputProps={{ readOnly: true }}
                        variant="filled"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Branch"
                        value={
                          isEdit
                            ? displayAcademicField(initialData?.branch, 'Not available')
                            : '—'
                        }
                        InputProps={{ readOnly: true }}
                        variant="filled"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Entry Type"
                        value="—"
                        InputProps={{ readOnly: true }}
                        variant="filled"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="College"
                        value={collegeDisplay}
                        InputProps={{ readOnly: true }}
                        variant="filled"
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {isEdit && (enrichedBatch || batchNameResolved) && (
                  <>
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle1"
                        component="h2"
                        fontWeight={600}
                        sx={{ mt: 1, mb: 0, letterSpacing: '-0.01em' }}
                      >
                        Coding batch
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Batch name"
                            value={batchNameResolved || '—'}
                            InputProps={{ readOnly: true }}
                            variant="filled"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Batch code"
                            value={
                              enrichedBatch?.code != null ? String(enrichedBatch.code) : '—'
                            }
                            InputProps={{ readOnly: true }}
                            variant="filled"
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    component="h2"
                    fontWeight={600}
                    sx={{ mt: 1, mb: 0, letterSpacing: '-0.01em' }}
                  >
                    Program
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Branch / Department"
                    name="branch"
                    value={form.branch}
                    onChange={set('branch')}
                    helperText="Select your branch"
                  >
                    {PROFILE_BRANCH_MENU_ITEMS.map(({ value, menuLabel }) => (
                      <MenuItem key={value} value={value}>
                        {menuLabel}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  {form.role === 'student' ? (
                    <TextField
                      fullWidth
                      label="Year of Study"
                      name="yearOfStudy"
                      type="number"
                      value={form.yearOfStudy}
                      onChange={set('yearOfStudy')}
                      placeholder="e.g., 3"
                      inputProps={{ min: 1, max: 5 }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: '100%',
                        minHeight: 56,
                        display: { xs: 'none', sm: 'flex' },
                        alignItems: 'flex-start',
                        pt: 0.5,
                      }}
                      aria-hidden
                    />
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Card sx={{ ...PROFILE_SUBCARD_SX, width: '100%', maxWidth: '100%' }}>
                    <CardContent sx={PROFILE_SUBCARD_CONTENT_SX}>
                      <Typography
                        variant="subtitle1"
                        component="h2"
                        fontWeight={600}
                        sx={{ letterSpacing: '-0.01em', mb: 1 }}
                      >
                        Administration
                      </Typography>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mb: 2, maxWidth: 560, lineHeight: 1.5 }}
                      >
                        Department, batch label, and section on the user record.
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          {deptOptions.length > 0 ? (
                            <TextField
                              select
                              fullWidth
                              label="Department"
                              value={form.department}
                              onChange={set('department')}
                            >
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>
                              {deptOptions.map((d) => (
                                <MenuItem key={d} value={d}>
                                  {d}
                                </MenuItem>
                              ))}
                            </TextField>
                          ) : (
                            <TextField
                              fullWidth
                              label="Department"
                              value={form.department}
                              onChange={set('department')}
                            />
                          )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          {batchOptions.length > 0 ? (
                            <TextField
                              select
                              fullWidth
                              label="Admin batch label"
                              value={form.batch}
                              onChange={set('batch')}
                            >
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>
                              {batchOptions.map((b) => (
                                <MenuItem key={b} value={b}>
                                  {b}
                                </MenuItem>
                              ))}
                            </TextField>
                          ) : (
                            <TextField
                              fullWidth
                              label="Admin batch label"
                              value={form.batch}
                              onChange={set('batch')}
                            />
                          )}
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Section"
                            name="section"
                            value={form.section}
                            onChange={set('section')}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <ProfileSubsectionCard
                    title="Coding profiles"
                    description="Optional links used for coding activity and integrations"
                    cardSx={{ width: '100%', maxWidth: '100%' }}
                  >
                    <TextField
                      fullWidth
                      label="LeetCode username"
                      value={form.leetcodeUsername}
                      onChange={set('leetcodeUsername')}
                      placeholder="e.g., username"
                    />
                  </ProfileSubsectionCard>
                </Grid>
              </Grid>

              <Box
                sx={{
                  mt: 4,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  type="button"
                  variant="outlined"
                  size="large"
                  onClick={onClose}
                  disabled={saving}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create user'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Dialog>
  );
}
